/**
 * SummaryTiles — four top-of-page metrics + overall GTM readiness.
 *
 * Overall readiness % is CALCULATED (never hard-coded) via the shared
 * client-side progress module which mirrors backend/services/gtm/progress.py.
 */
import { CheckCircle2, Activity, OctagonX, Target } from "lucide-react";

function Tile({ testid, label, value, sub, Icon, tone }) {
    const toneCls = {
        emerald: "text-emerald-300",
        cyan: "text-qoryn-cyan",
        red: "text-red-300",
        amber: "text-amber-300",
        muted: "text-qoryn-textDim",
    }[tone] ?? "text-qoryn-text";
    return (
        <div data-testid={testid} className="qoryn-panel rounded-2xl p-4 flex items-center gap-4">
            <span className={`flex items-center justify-center h-10 w-10 rounded-lg bg-white/[0.03] ${toneCls}`}>
                <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
                <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                    {label}
                </div>
                <div className="font-display text-[26px] leading-none tracking-tight text-qoryn-text mt-1">
                    {value}
                </div>
                {sub && (
                    <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted mt-1">
                        {sub}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function SummaryTiles({ summary }) {
    if (!summary) return null;
    const pct = Math.round(summary.overall_readiness * 100);
    return (
        <div data-testid="gtm-summary-tiles" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div data-testid="gtm-overall-readiness" className="qoryn-panel rounded-2xl p-4 lg:col-span-2 relative overflow-hidden">
                <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                    Overall GTM Readiness
                </div>
                <div className="flex items-baseline gap-3 mt-2">
                    <span className="font-display text-[48px] leading-none font-light tracking-tight text-qoryn-text">
                        {pct}
                    </span>
                    <span className="font-display text-[22px] leading-none text-qoryn-textDim">%</span>
                </div>
                <div className="mt-3 h-1.5 w-full rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                        data-testid="gtm-overall-readiness-bar"
                        className="h-full bg-qoryn-cyan/70 transition-[width] duration-500 ease-out"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <div className="mt-2 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    Calculated · never asserted
                </div>
            </div>
            <Tile
                testid="gtm-tile-completed"
                label="Completed Milestones"
                value={summary.completed_count}
                sub={`of ${summary.total_count}`}
                Icon={CheckCircle2}
                tone="emerald"
            />
            <Tile
                testid="gtm-tile-active"
                label="Active Work"
                value={summary.active_count}
                Icon={Activity}
                tone="cyan"
            />
            <Tile
                testid="gtm-tile-blockers"
                label="Blocking Gates"
                value={summary.blocking_count}
                sub={`${summary.launch_critical_remaining} launch-critical remaining`}
                Icon={summary.blocking_count > 0 ? OctagonX : Target}
                tone={summary.blocking_count > 0 ? "red" : "amber"}
            />
        </div>
    );
}
